import axios, { AxiosInstance, AxiosError } from 'axios';
import { 
  SecureTokenStorage, 
  isSecureUrl,
  CSRFProtection,
  SecurityMonitor,
  validateSecureForm
} from '../utils/security';

// Force HTTPS in production
const API_BASE_URL = (() => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
  
  // Force HTTPS in production
  if (import.meta.env.PROD && baseUrl.startsWith('http://')) {
    console.warn('Forcing HTTPS in production environment');
    return baseUrl.replace('http://', 'https://');
  }
  
  // Validate URL security
  if (!isSecureUrl(baseUrl)) {
    throw new Error('Insecure API base URL detected');
  }
  
  return baseUrl;
})();

// Request cache for GET requests
const requestCache = new Map<string, { data: any; timestamp: number; ttl: number }>();

// Request deduplication for identical requests
const pendingRequests = new Map<string, Promise<any>>();

// Cache utilities
function getCacheKey(url: string, params?: any): string {
  return `${url}${params ? JSON.stringify(params) : ''}`;
}

function getCachedData(key: string): any | null {
  const cached = requestCache.get(key);
  if (!cached) return null;

  const now = Date.now();
  if (now > cached.timestamp + cached.ttl) {
    requestCache.delete(key);
    return null;
  }

  return cached.data;
}

function setCachedData(key: string, data: any, ttlMs: number = 300000): void {
  requestCache.set(key, {
    data,
    timestamp: Date.now(),
    ttl: ttlMs,
  });
}

// Create secure axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
    // 'X-Requested-With': 'XMLHttpRequest', // Removed to avoid CORS issues
  },
  // withCredentials: true, // Disabled to avoid CORS issues with wildcard origin
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// Secure request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add secure authentication token
    const token = SecureTokenStorage.getAccessToken();
    if (token) {
      // Validate token before using
      if (!SecureTokenStorage.isTokenExpired(token)) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        // Token expired, clear it
        SecureTokenStorage.clearAll();
        SecurityMonitor.logSecurityEvent({
          type: 'suspicious_activity',
          details: 'Expired token detected and cleared',
        });
      }
    }
    
    // Remove CSRF and Request ID headers to avoid CORS issues
    // const csrfToken = CSRFProtection.getToken();
    // if (csrfToken) {
    //   config.headers['X-CSRF-Token'] = csrfToken;
    // }
    
    // config.headers['X-Request-ID'] = crypto.randomUUID();
    
    // Sanitize request data
    if (config.data && typeof config.data === 'object') {
      const validation = validateSecureForm(config.data);
      if (!validation.isValid) {
        SecurityMonitor.logSecurityEvent({
          type: 'xss_attempt',
          details: `Request validation failed: ${validation.errors.join(', ')}`,
        });
        return Promise.reject(new Error('Request contains invalid data'));
      }
      config.data = validation.sanitizedData;
    }
    
    return config;
  },
  (error) => {
    SecurityMonitor.logSecurityEvent({
      type: 'suspicious_activity',
      details: `Request interceptor error: ${error.message}`,
    });
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // 401エラーのみリトライ、403エラーはリトライしない
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = SecureTokenStorage.getRefreshToken();
        
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Validate refresh token before using
        if (SecureTokenStorage.isTokenExpired(refreshToken)) {
          throw new Error('Refresh token expired');
        }

        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        }, {
          headers: {
            'Content-Type': 'application/json',
            // Remove custom headers to avoid CORS issues
            // 'X-Requested-With': 'XMLHttpRequest',
            // ...CSRFProtection.getHeaders(),
          },
          // withCredentials: true, // Disabled to avoid CORS issues
        });

        const { token: newToken, refreshToken: newRefreshToken } = response.data;
        
        // Securely update tokens
        SecureTokenStorage.setAccessToken(newToken);
        SecureTokenStorage.setRefreshToken(newRefreshToken);
        
        // Update default header
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        // Process queued requests
        processQueue(null, newToken);
        
        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens securely
        SecureTokenStorage.clearAll();
        CSRFProtection.clearToken();
        delete apiClient.defaults.headers.common['Authorization'];
        
        // Log security event
        SecurityMonitor.logSecurityEvent({
          type: 'suspicious_activity',
          details: 'Token refresh failed, clearing session',
        });
        
        processQueue(refreshError, null);
        
        // Trigger logout in auth context
        window.dispatchEvent(new CustomEvent('auth:logout'));
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Enhanced API service with better error handling and caching
class ApiService {
  async get(endpoint: string, options?: { cache?: boolean; cacheTtl?: number }) {
    const cacheKey = getCacheKey(endpoint);
    
    // Check cache first for GET requests
    if (options?.cache !== false) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        return { data: cachedData };
      }
    }
    
    // Check for pending identical requests
    if (pendingRequests.has(cacheKey)) {
      const data = await pendingRequests.get(cacheKey);
      return { data };
    }
    
    try {
      const requestPromise = apiClient.get(endpoint).then(response => response.data);
      pendingRequests.set(cacheKey, requestPromise);
      
      const data = await requestPromise;
      
      // Cache successful GET responses
      if (options?.cache !== false) {
        setCachedData(cacheKey, data, options?.cacheTtl);
      }
      
      return { data };
    } catch (error) {
      throw this.parseError(error);
    } finally {
      pendingRequests.delete(cacheKey);
    }
  }

  async post(endpoint: string, data?: any) {
    try {
      const response = await apiClient.post(endpoint, data);
      
      // Invalidate related cache entries
      this.invalidateCache(endpoint);
      
      return { data: response.data };
    } catch (error) {
      throw this.parseError(error);
    }
  }

  async put(endpoint: string, data?: any) {
    try {
      const response = await apiClient.put(endpoint, data);
      
      // Invalidate related cache entries
      this.invalidateCache(endpoint);
      
      return { data: response.data };
    } catch (error) {
      throw this.parseError(error);
    }
  }

  async delete(endpoint: string) {
    try {
      const response = await apiClient.delete(endpoint);
      
      // Invalidate related cache entries
      this.invalidateCache(endpoint);
      
      return { data: response.data };
    } catch (error) {
      throw this.parseError(error);
    }
  }

  // Cache invalidation for mutations
  private invalidateCache(endpoint: string): void {
    const baseEndpoint = endpoint.split('/').slice(0, -1).join('/');
    
    // Remove cache entries that might be affected by this mutation
    for (const [key] of requestCache) {
      if (key.includes(baseEndpoint)) {
        requestCache.delete(key);
      }
    }
  }

  // Manual cache management
  clearCache(): void {
    requestCache.clear();
    pendingRequests.clear();
  }

  invalidateCachePattern(pattern: string): void {
    for (const [key] of requestCache) {
      if (key.includes(pattern)) {
        requestCache.delete(key);
      }
    }
  }

  private parseError(error: any) {
    if (axios.isAxiosError(error)) {
      // Return structured error that can be handled by error utilities
      if (error.response?.data?.error) {
        return error.response.data.error;
      }
      
      // Handle HTTP status codes
      const status = error.response?.status;
      if (status === 401) {
        return {
          code: 'AUTH_EXPIRED_TOKEN',
          message: 'Authentication required',
        };
      }
      
      if (status === 403) {
        return {
          code: 'AUTH_INSUFFICIENT_PERMISSIONS',
          message: 'Insufficient permissions',
        };
      }
      
      if (status === 404) {
        return {
          code: 'RESOURCE_NOT_FOUND',
          message: 'Resource not found',
        };
      }
      
      if (status && status >= 500) {
        return {
          code: 'INTERNAL_ERROR',
          message: 'Internal server error',
        };
      }
      
      // Network errors
      if (error.code === 'NETWORK_ERROR' || !error.response) {
        return {
          code: 'NETWORK_ERROR',
          message: 'Network error occurred',
        };
      }
      
      // Timeout errors
      if (error.code === 'ECONNABORTED') {
        return {
          code: 'TIMEOUT_ERROR',
          message: 'Request timeout',
        };
      }
      
      return {
        code: 'HTTP_ERROR',
        message: error.message,
      };
    }
    
    return {
      code: 'UNKNOWN_ERROR',
      message: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

export const apiService = new ApiService();